import { DeleteObjectCommand, HeadBucketCommand, PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { getSysCredentials } from "./SysCredentialsService";

const S3Service = {
    // Load AWS credentials from the credentials endpoint
    loadCredentials: async () => {
        try {
            return await getSysCredentials();
        } catch (error) {
            console.error('Error loading AWS credentials:', error);
            throw new Error('Failed to load AWS credentials');
        }
    },

    // Upload file to S3
    uploadToS3: async (file, fileName, folder = 'avatar') => {
        try {
            console.log('Starting S3 upload...', { fileName, folder, fileSize: file.size, fileType: file.type });
            
            // Load credentials
            const credentials = await S3Service.loadCredentials();
            console.log('Credentials loaded:', { 
                hasAccessKey: !!credentials.accessKey, 
                hasSecret: !!credentials.accessSecret 
            });

            // Create S3 client with credentials
            const s3Client = new S3Client({
                region: credentials.region,
                credentials: {
                    accessKeyId: credentials.accessKey,
                    secretAccessKey: credentials.accessSecret,
                },
            });

            const bucketName = 'puzzleinteract-images';
            const key = `${folder}/${fileName}`;
            
            console.log('Upload parameters:', { bucketName, key, contentType: file.type });

            // Create the command for uploading
            const command = new PutObjectCommand({
                Bucket: bucketName,
                Key: key,
                Body: await file.arrayBuffer(),
                ContentType: file.type,
                ACL: 'public-read',
            });

            console.log('Sending command to S3...');
            
            // Upload the file directly to S3
            const result = await s3Client.send(command);
            console.log('S3 upload successful:', result);

            // Return the public URL
            return  {
                url: `${credentials.bucketEndpoint}/${folder}/${fileName}`,
                fileName: fileName,
            };
        } catch (error) {
            console.error('S3 upload error details:', {
                error: error.message,
                code: error.code,
                name: error.name,
                stack: error.stack,
                fileName,
                folder
            });
            
            // Provide more specific error messages
            if (error.message.includes('Failed to fetch')) {
                throw new Error('Network error: Unable to connect to AWS S3. Please check your internet connection and try again.');
            } else if (error.code === 'CredentialsProviderError') {
                throw new Error('AWS credentials error: Please check your credentials configuration.');
            } else if (error.code === 'NoSuchBucket') {
                throw new Error('S3 bucket not found: The specified bucket does not exist.');
            } else if (error.code === 'AccessDenied') {
                throw new Error('Access denied: Insufficient permissions to upload to S3.');
            } else {
                throw new Error(`S3 upload failed: ${error.message}`);
            }
        }
    },

    // Upload avatar specifically
    uploadAvatar: async (file) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            throw new Error('Please select an image file');
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            throw new Error('File size must be less than 5MB');
        }

        // Generate unique filename
        const timestamp = Date.now();
        const fileExtension = file.name.split('.').pop();
        const fileName = `${timestamp}.${fileExtension}`;

        // Upload to S3
        return await S3Service.uploadToS3(file, fileName, 'avatar');
    },

    uploadTutorLogo: async (file) => {
        if (!file.type.startsWith("image/")) {
            throw new Error("Please select an image file");
        }
        if (file.size > 2 * 1024 * 1024) {
            throw new Error("Logo must be less than 2MB");
        }
        const timestamp = Date.now();
        const fileExtension = file.name.split(".").pop();
        const fileName = `${timestamp}.${fileExtension}`;
        const result = await S3Service.uploadToS3(file, fileName, "tutor-logo");
        return typeof result === "string" ? result : result?.url;
    },

    // Delete file from S3
    deleteFromS3: async (fileName, folder = 'uploads') => {
        try {
            // Load credentials
            const credentials = await S3Service.loadCredentials();

            // Create S3 client with credentials
            const s3Client = new S3Client({
                region: 'us-east-1',
                credentials: {
                    accessKeyId: credentials.accessKey,
                    secretAccessKey: credentials.accessSecret,
                },
            });

            const bucketName = 'puzzleinteract-images';
            const key = `${folder}/${fileName}`;

            // Create the command for deleting
            const command = new DeleteObjectCommand({
                Bucket: bucketName,
                Key: key,
            });

            // Delete the file from S3
            await s3Client.send(command);

            return true;
        } catch (error) {
            console.error('S3 delete error:', error);
            throw error;
        }
    },

    // Test S3 connection and credentials
    testConnection: async () => {
        try {
            console.log('Testing S3 connection...');
            
            // Load credentials
            const credentials = await S3Service.loadCredentials();
            
            // Create S3 client
            const s3Client = new S3Client({
                region: 'us-east-1',
                credentials: {
                    accessKeyId: credentials.accessKey,
                    secretAccessKey: credentials.accessSecret,
                },
            });

            const bucketName = 'puzzleinteract-images';
            
            // Test bucket access
            const command = new HeadBucketCommand({ Bucket: bucketName });
            const result = await s3Client.send(command);
            
            console.log('S3 connection test successful:', result);
            return { success: true, message: 'S3 connection successful' };
        } catch (error) {
            console.error('S3 connection test failed:', error);
            return { 
                success: false, 
                message: error.message,
                code: error.code 
            };
        }
    }
};

export default S3Service;
